import React, { useCallback, useMemo, useState } from 'react'
import { Select } from 'antd'
import { Icon } from '@iconify/react'

import styles from './ProjectMultiSelect.module.css'

const DropdownLoader = () => (
    <div className={styles.dropdownLoading}>
        <Icon
            icon="line-md:loading-loop"
            className={styles.dropdownLoadingIcon}
        />
    </div>
)

export const buildProjectOptionLabel = (name, colorCode) => {
    const hex =
        typeof colorCode === 'string' && colorCode.trim()
            ? colorCode.trim()
            : null

    return (
        <span className={styles.projectOptionLabel}>
            {hex ? (
                <span
                    className={styles.projectColorSwatch}
                    style={{ backgroundColor: hex }}
                    aria-hidden
                />
            ) : null}
            {name || 'Untitled'}
        </span>
    )
}

/**
 * @param {Array<{ _id?: string, projectId?: string, value?: string, name?: string, projectName?: string, label?: React.ReactNode, colorCode?: string }>} projects
 */
export const buildProjectSelectOptions = (projects = []) =>
    (projects || [])
        .map((project) => {
            const value =
                project?.value ?? project?._id ?? project?.projectId ?? null
            if (!value) return null

            if (project?.label != null) {
                return { value, label: project.label }
            }

            const name =
                project?.name ?? project?.projectName ?? 'Untitled Project'

            return {
                value,
                label: buildProjectOptionLabel(name, project?.colorCode),
            }
        })
        .filter(Boolean)

const ProjectMultiSelect = ({
    value,
    onChange,
    options,
    projects,
    placeholder = 'Select projects',
    variant = 'toolbar',
    wrapClassName,
    className,
    maxTagCount = 'responsive',
    allowClear = true,
    showSearch = false,
    filterOption,
    loading = false,
    page = 1,
    emptyText = 'No projects found',
    notFoundContent,
    popupRender,
    onSearch,
    onOpenChange,
    ...rest
}) => {
    const [searchText, setSearchText] = useState('')

    const resolvedOptions = useMemo(() => {
        if (options !== undefined) return options
        return buildProjectSelectOptions(projects)
    }, [options, projects])

    const resolvedFilterOption =
        filterOption !== undefined ? filterOption : showSearch ? false : undefined

    const isInitialLoading = loading && page === 1
    const isLoadingMore = loading && page > 1
    const hasSearchText = Boolean(String(searchText || '').trim())

    const resolvedNotFoundContent = useMemo(() => {
        if (notFoundContent !== undefined) return notFoundContent
        return isInitialLoading ? <DropdownLoader /> : emptyText
    }, [notFoundContent, isInitialLoading, emptyText])

    const resolvedPopupRender = useCallback(
        (menu) => {
            if (popupRender) return popupRender(menu)
            return (
                <>
                    {menu}
                    {isLoadingMore ? (
                        <div className={styles.dropdownLoadingMore}>
                            <Icon
                                icon="line-md:loading-loop"
                                className={styles.dropdownLoadingIcon}
                            />
                        </div>
                    ) : null}
                </>
            )
        },
        [popupRender, isLoadingMore]
    )

    const handleSearch = useCallback(
        (text) => {
            setSearchText(text || '')
            onSearch?.(text)
        },
        [onSearch]
    )

    const handleOpenChange = useCallback(
        (open) => {
            if (!open) setSearchText('')
            onOpenChange?.(open)
        },
        [onOpenChange]
    )

    const isFormVariant = variant === 'form'

    const handleTagClose = (event, onClose) => {
        event.preventDefault()
        event.stopPropagation()
        onClose?.(event)
    }

    const tagRender = ({ label, closable, onClose }) => (
        <span className={styles.projectTag}>
            <span className={styles.projectTagText}>{label}</span>
            {closable ? (
                <Icon
                    icon="mdi:close"
                    role="button"
                    tabIndex={-1}
                    aria-label="Remove project"
                    className={styles.projectTagCloseIcon}
                    onMouseDown={(event) => handleTagClose(event, onClose)}
                />
            ) : null}
        </span>
    )

    const wrapClass = [
        wrapClassName || (isFormVariant ? styles.wrapForm : styles.wrap),
        hasSearchText ? styles.hasSearchText : styles.searchIdle,
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div className={wrapClass}>
            <Select
                mode="multiple"
                allowClear={allowClear}
                size={isFormVariant ? 'large' : 'middle'}
                placeholder={placeholder}
                className={`${isFormVariant ? styles.selectForm : styles.select} ${className || ''}`}
                options={resolvedOptions}
                value={value}
                maxTagCount={maxTagCount}
                showSearch={showSearch}
                filterOption={resolvedFilterOption}
                tagRender={tagRender}
                suffixIcon={
                    <Icon
                        icon="mdi:chevron-down"
                        width={18}
                        height={18}
                        className={styles.chevron}
                    />
                }
                onChange={(ids) => onChange?.(ids || [])}
                {...rest}
                onSearch={handleSearch}
                onOpenChange={handleOpenChange}
                loading={isInitialLoading}
                notFoundContent={resolvedNotFoundContent}
                popupRender={resolvedPopupRender}
            />
        </div>
    )
}

export default ProjectMultiSelect
